import { Lock, LogOut, X } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface PasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => Promise<void>;
}

export const PasswordModal: React.FC<PasswordModalProps> = ({ isOpen, onClose, onLogout }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleLogoutClick = async () => {
    await onLogout();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-6">
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            <h2 className="font-semibold text-xl">{t("common.password")}</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <p className="mb-4 text-gray-600 text-sm">{t("common.passwordChangeDescription")}</p>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <h3 className="mb-2 font-medium text-blue-900 text-sm">
                {t("common.passwordChangeSteps")}
              </h3>
              <ol className="list-inside list-decimal space-y-1 text-blue-800 text-sm">
                <li>{t("common.passwordChangeStep1")}</li>
                <li>{t("common.passwordChangeStep2")}</li>
                <li>{t("common.passwordChangeStep3")}</li>
                <li>{t("common.passwordChangeStep4")}</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t bg-gray-50 p-6">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleLogoutClick}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            {t("common.logoutAndChangePassword")}
          </Button>
        </div>
      </div>
    </div>
  );
};
